import React, { useState, useEffect, useMemo } from 'react';
import { Layout, Menu, Avatar, Typography, Divider } from 'antd';
import { 
  DashboardOutlined, 
  FileTextOutlined, 
  CheckCircleOutlined, 
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  MessageOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  BranchesOutlined,  // 添加這個圖標
  ClockCircleOutlined,  // 添加時鐘圖標用於待處理事項
  RocketOutlined,  // 添加火箭圖標用於 Published Apps
  AppstoreOutlined,  // 添加應用圖標
  AppstoreAddOutlined,
  ToolOutlined,  // 添加工具圖標用於 Studio
  SettingOutlined,  // 添加設定圖標用於管理工具
  ContactsOutlined,  // 添加聯絡人圖標
  TeamOutlined,  // 添加群組圖標
  TagsOutlined,  // 添加標籤圖標
  SendOutlined,  // 添加發送圖標
  SafetyOutlined,  // 添加安全圖標用於權限管理
  ApiOutlined,
  LineChartOutlined,  // 添加圖表圖標用於報表
  FileImageOutlined,  // 添加圖片圖標用於 App's Media Reports
  FormOutlined,  // 表單實例報表
  InsertRowAboveOutlined,  // 表單樞紐分析報表
  UploadOutlined,
  IdcardOutlined,
  ShopOutlined,
  DollarOutlined,
  FundOutlined,
  FolderOpenOutlined,
  PictureOutlined,
  ReadOutlined,
  CalendarOutlined,
  FolderViewOutlined,
  FileWordOutlined,
  ProfileOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import UserAvatar from './UserAvatar';
import { filterMenuItemsByPermission, getUserInterfacesFromStorage } from '../utils/permissionUtils';

const { Sider } = Layout;
const { Text } = Typography;

const MOBILE_BREAKPOINT = 768;

const SideMenu = ({ userInfo, onLogout, onMenuSelect, selectedKey, onAvatarClick }) => {
  const [collapsed, setCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  const [openKeys, setOpenKeys] = useState([]);
  const [userInterfaces, setUserInterfaces] = useState([]);
  const { t } = useLanguage();

  // 獲取用戶權限（總是從 API 獲取最新權限）
  useEffect(() => {
    const loadUserInterfaces = async () => {
      try {
        console.log('[SideMenu] 開始載入用戶權限，userInfo:', userInfo);
        // 強制從 API 獲取最新權限，不使用緩存
        const interfaces = await getUserInterfacesFromStorage(true);
        console.log('[SideMenu] 獲取到的權限列表:', interfaces);
        setUserInterfaces(interfaces);
      } catch (error) {
        console.error('[SideMenu] 載入用戶權限失敗:', error);
        setUserInterfaces([]);
      }
    };

    if (userInfo) {
      loadUserInterfaces();
    } else {
      console.warn('[SideMenu] userInfo 為空，無法載入權限');
      setUserInterfaces([]);
    }
  }, [userInfo]);

  // 處理選單展開/收合 - 手風琴效果
  const handleOpenChange = (keys) => {
    const mainMenus = ['application', 'studio', 'commerce', 'reports', 'adminTools'];
    
    // 找出最新展開的選單
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    
    if (latestOpenKey && mainMenus.includes(latestOpenKey)) {
      // 如果展開的是主選單，則只保留當前展開的主選單（手風琴效果）
      setOpenKeys([latestOpenKey]);
    } else if (latestOpenKey) {
      // 如果展開的是子選單，保持當前主選單展開狀態
      const currentMainMenu = openKeys.find(key => mainMenus.includes(key));
      if (currentMainMenu) {
        setOpenKeys([currentMainMenu, ...keys.filter(key => !mainMenus.includes(key))]);
      } else {
        setOpenKeys(keys);
      }
    } else {
      // 收合選單
      setOpenKeys(keys);
    }
  };

  // 構建基礎菜單項
  const baseMenuItems = useMemo(() => {
    const adminToolsChildren = [
      // 聯絡人管理
      {
        key: 'contactList',
        icon: <ContactsOutlined />,
        label: t('menu.contactList'),
        url: '/contacts',
      },
      // 廣播群組管理
      {
        key: 'broadcastGroups',
        icon: <TeamOutlined />,
        label: t('menu.broadcastGroups'),
        url: '/broadcast-groups',
      },
      // 標籤管理
      {
        key: 'hashtags',
        icon: <TagsOutlined />,
        label: t('menu.hashtags'),
        url: '/hashtags',
      },
      // 公司/用戶管理
      {
        key: 'companyUserAdmin',
        icon: <UserOutlined />,
        label: t('menu.companyUserAdmin'),
        url: '/company-user-admin',
      },
      // 權限管理（會根據用戶權限自動過濾）
      {
        key: 'permissionManagement',
        icon: <SafetyOutlined />,
        label: t('menu.permissionManagement'),
        url: '/permission-management',
      },
      {
        key: 'apiProviders',
        icon: <ApiOutlined />,
        label: t('menu.apiProviders'),
        url: '/api-providers',
      },
      // WhatsApp 電話號碼驗證管理
      {
        key: 'phoneVerificationAdmin',
        icon: <MessageOutlined />,
        label: t('menu.phoneVerificationAdmin'),
        url: '/phone-verification-admin',
      },
      {
        key: 'admin.usageBilling.settings',
        icon: <DollarOutlined />,
        label: t('menu.usageBillingSettings') || '用量計費單價',
        url: '/admin/usage-billing-settings',
      }
    ];

    return [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: t('menu.dashboard'),
      },
      {
        type: 'divider',
      },
      // Application 應用區域
      {
        key: 'application',
        icon: <AppstoreOutlined />,
        label: t('menu.application'),
        children: [
          {
            key: 'publishedApps',
            icon: <RocketOutlined />,
            label: t('menu.publishedApps'),
            url: '/published-apps',
          },
          {
            key: 'pendingTasks',
            icon: <ClockCircleOutlined />,
            label: t('menu.pendingTasks'),
            url: '/pending-tasks',
          },
          {
            key: 'workflowMonitor',
            icon: <BarChartOutlined />,
            label: t('menu.runningApps'),
            url: '/workflow-monitor',
          },
          {
            key: 'chatBotInbox',
            icon: <MessageOutlined />,
            label: t('menu.chatBotInbox') || 'ChatBot 對話',
            url: '/chatbot-inbox',
          },
          {
            key: 'calendar',
            icon: <CalendarOutlined />,
            label: t('menu.calendar') || 'Calendar',
            url: '/calendar',
          },
          {
            key: 'documentLibrary',
            icon: <FolderViewOutlined />,
            label: t('menu.documentLibrary') || '文檔中心',
            url: '/document-library',
          }
        ]
      },
      {
        type: 'divider',
      },
      // Studio 工作室區域
      {
        key: 'studio',
        icon: <ToolOutlined />,
        label: t('menu.studio'),
        children: [
          {
            key: 'buildApp',
            icon: <AppstoreAddOutlined />,
            label: t('menu.buildApp') || 'Build App',
            url: '/studio/build-app',
          },
          {
            key: 'eformList',
            icon: <FileTextOutlined />,
            label: t('menu.eformList'),
            url: '/eform-list',
          },
          {
            key: 'whatsappTemplates',
            icon: <MessageOutlined />,
            label: t('menu.whatsappTemplates'),
            url: '/whatsapp-templates',
          },
          {
            key: 'whatsappWorkflow',
            icon: <BranchesOutlined />,
            label: t('menu.whatsappWorkflow'),
            url: '/workflow-list',
          },
          {
            key: 'dataSets',
            icon: <DatabaseOutlined />,
            label: t('dataSetManagement.title'),
            url: '/data-sets',
          },
          {
            key: 'aiEmployeeProfiles',
            icon: <IdcardOutlined />,
            label: t('menu.aiEmployeeProfiles'),
            url: '/ai-employee-profiles',
          },
          {
            key: 'knowledgeBase',
            icon: <ReadOutlined />,
            label: t('menu.knowledgeBase'),
            url: '/studio/knowledge-base',
          },
          {
            key: 'reportTemplates',
            icon: <FileWordOutlined />,
            label: t('menu.reportTemplates'),
            url: '/studio/report-templates',
          },
          {
            key: 'workflowPackage',
            icon: <FolderOpenOutlined />,
            label: t('menu.workflowPackage'),
            url: '/studio/workflow-package',
          }
        ]
      },
      {
        type: 'divider',
      },
      // Commerce 商務
      {
        key: 'commerce',
        icon: <ShopOutlined />,
        label: t('menu.commerce') || 'Commerce',
        children: [
          { key: 'commerce.salesOrders', icon: <FileTextOutlined />, label: t('menu.commerceSalesOrders') || 'Sales Orders', url: '/commerce/sales-orders' },
          { key: 'commerce.customers', icon: <ContactsOutlined />, label: t('menu.commerceCustomers') || 'Customers', url: '/commerce/customers' },
          { key: 'commerce.priceCards', icon: <DollarOutlined />, label: t('menu.commercePriceCards') || 'Price Cards', url: '/commerce/price-cards' },
          { key: 'commerce.collections', icon: <TagsOutlined />, label: t('menu.commerceCollections') || 'Collections', url: '/commerce/collections' },
          { key: 'commerce.products', icon: <FileTextOutlined />, label: t('menu.commerceProducts') || 'Products', url: '/commerce/products' },
          { key: 'commerce.deliveryRules', icon: <ClockCircleOutlined />, label: t('menu.commerceDeliveryRules') || 'Delivery cutoffs', url: '/commerce/delivery-rules' },
          { key: 'commerce.wpaHomePromotions', icon: <PictureOutlined />, label: t('menu.commerceWpaHomePromotions') || 'PWA home ads', url: '/commerce/wpa-home-promotions' },
          { key: 'commerce.import', icon: <UploadOutlined />, label: t('menu.commerceImport') || '資料交換', url: '/commerce/import' },
          { key: 'commerce.catalogAdmin', icon: <AppstoreOutlined />, label: t('menu.commerceCatalogAdmin') || 'Mete 目錄管理', url: '/commerce/catalog-admin' },
          { key: 'commerce.wpaPermissions', icon: <ContactsOutlined />, label: t('menu.commerceWpaPermissions') || 'PWA Permissions', url: '/commerce/wpa-permissions' },
        ]
      },
      {
        type: 'divider',
      },
      // Reports 報表區域
      {
        key: 'reports',
        icon: <LineChartOutlined />,
        label: t('menu.reports'),
        children: [
          {
            key: 'reports.realtime',
            icon: <RocketOutlined />,
            label: t('menu.realtimeReports'),
            url: '/reports/realtime',
          },
          {
            key: 'reports.daily',
            icon: <LineChartOutlined />,
            label: t('menu.dailyReports'),
            url: '/reports/daily',
          },
          {
            key: 'reports.monthly',
            icon: <BarChartOutlined />,
            label: t('menu.monthlyReports'),
            url: '/reports/monthly',
          },
          {
            key: 'reports.appsMedia',
            icon: <FileImageOutlined />,
            label: t('menu.appsMediaReports') || 'App\'s Media Reports',
            url: '/reports/apps-media',
          },
          {
            key: 'reports.templateReport',
            icon: <ProfileOutlined />,
            label: t('menu.templateReport'),
            url: '/reports/template-report',
          },
          {
            key: 'reports.eformInstances',
            icon: <FormOutlined />,
            label: t('menu.eformInstanceReports') || 'EForm Instance Reports',
            url: '/reports/eform-instances',
          },
          {
            key: 'reports.eformPivot',
            icon: <InsertRowAboveOutlined />,
            label: t('menu.eformPivotReports') || 'EForm Pivot Reports',
            url: '/reports/eform-pivot',
          },
          {
            key: 'reports.commerce',
            icon: <FundOutlined />,
            label: t('menu.commerceReports') || 'Commerce Reports',
            url: '/reports/commerce',
          },
          {
            key: 'reports.billingSystem',
            icon: <FundOutlined />,
            label: t('menu.reportsBillingSystem') || '全系統用量計費',
            url: '/reports/billing-system',
          }
        ]
      },
      {
        type: 'divider',
      },
      // Administrator Tools 管理工具區域
      {
        key: 'adminTools',
        icon: <SettingOutlined />,
        label: t('menu.adminTools'),
        children: adminToolsChildren
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('menu.logout'),
        onClick: onLogout,
      }
    ];
  }, [userInfo, t]);

  // 根據權限過濾菜單項
  const menuItems = useMemo(() => {
    if (!userInterfaces || userInterfaces.length === 0) {
      // 如果還沒有載入權限，返回空菜單或僅顯示 logout
      return [
        {
          key: 'logout',
          icon: <LogoutOutlined />,
          label: t('menu.logout'),
          onClick: onLogout,
        }
      ];
    }

    // 傳入 userInfo 以便進行角色檢查（特別是 phoneVerificationAdmin 需要 Tenant_Admin 角色）
    return filterMenuItemsByPermission(baseMenuItems, userInterfaces, userInfo);
  }, [baseMenuItems, userInterfaces, userInfo, t, onLogout]);

  return (
    <Sider 
      trigger={null} 
      collapsible 
      collapsed={collapsed}
      width={280}
      style={{
        background: '#3d186b',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
        position: 'fixed',
        height: '100vh',
        left: 0,
        top: 0,
        zIndex: 1000,
      }}
    >
      {/* WhatoFlow Logo + 展開收合按鈕 */}
      <div style={{ 
        padding: collapsed ? '12px 8px' : '16px 16px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        gap: 8,
      }}>
        {!collapsed && (
          <img 
            src="/assets/Whatoflow_logo_W.png" 
            alt="WhatoFlow Logo" 
            style={{
              width: '200px',
              height: 'auto',
              objectFit: 'contain',
              transition: 'all 0.3s ease',
              flexShrink: 0,
            }}
          />
        )}
        <div
          onClick={() => setCollapsed(!collapsed)}
          style={{
            height: 32,
            minWidth: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 6,
            cursor: 'pointer',
            transition: 'all 0.3s',
            color: 'rgba(255, 255, 255, 0.65)',
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.65)';
          }}
        >
          {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        </div>
      </div>

      {/* 用戶資訊區域 */}
      <div style={{ 
        padding: collapsed ? '12px 8px' : '16px 16px',
        textAlign: collapsed ? 'center' : 'left',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: 16
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <div style={{ cursor: 'pointer' }} onClick={onAvatarClick} title="個人偏好">
            <Avatar 
              size={collapsed ? 32 : 40} 
              src={userInfo?.avatar_url ? userInfo.avatar_url : undefined}
              icon={!userInfo?.avatar_url && <UserOutlined />}
              style={{ backgroundColor: '#7234CF' }}
            />
          </div>
          {!collapsed && (
            <div style={{ marginLeft: 12, flex: 1 }}>
              <Text style={{ color: 'white', fontSize: 14, fontWeight: 500, display: 'block' }}>
                {userInfo?.name || 'User'}
              </Text>
            </div>
          )}
        </div>
      </div>

      {/* 選單容器 - 添加滾動功能 */}
      <div 
        style={{
          height: 'calc(100vh - 200px)', // 減去 logo 和用戶資訊區域的高度
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingRight: '4px', // 為滾動條留出空間
        }}
        className="custom-scrollbar"
      >
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={menuItems}
          onClick={({ key, keyPath }) => {
            console.log('Menu click:', { key, keyPath, collapsed });
            
            if (key === 'logout') {
              onLogout();
            } else if (key === 'application' || key === 'studio' || key === 'commerce' || key === 'reports' || key === 'adminTools') {
              if (collapsed) {
                setOpenKeys([key]);
              }
              return;
            } else {
              // keyPath 數組的最後一個元素才是實際點擊的子菜單 key
              // keyPath 格式: [父菜單key, 子菜單key] 或 [子菜單key]
              const actualKey = key; // 直接使用點擊的 key，因為這已經是子菜單的 key
              console.log('Calling onMenuSelect with:', actualKey);
              onMenuSelect(actualKey);
            }
          }}
          style={{
            border: 'none',
            background: 'transparent',
            height: '100%',
          }}
        />
      </div>
    </Sider>
  );
};

export default SideMenu; 